export default {
  code: 'en',
  meta: {
    title: 'Nanovitsa (Нановица), Bulgaria – Village Guide to Tatul, Momchilgrad & Kardzhali',
    description:
      'Guide to Nanovitsa (Нановица), a village in Momchilgrad Municipality, Kardzhali Province, Bulgaria: facts, history, photos, an interactive map and the nearby sights – Tatul and the sanctuary of Orpheus, Momchilgrad, Kardzhali, Perperikon and more.',
    keywords:
      'Nanovitsa, Нановица, Nanovitsa Bulgaria, Tatul, Orpheus sanctuary, Momchilgrad, Kardzhali, Kırcaali, Perperikon, Eastern Rhodopes, Bulgarian village guide',
    ogTitle: 'Nanovitsa (Нановица) – Village Guide to Tatul, Momchilgrad & Kardzhali',
    ogDescription:
      'Facts, history, photos and an interactive map of Nanovitsa and the sights around it in the Eastern Rhodopes of southern Bulgaria.',
    twitterTitle: 'Nanovitsa (Нановица) – Village Guide, Bulgaria',
    twitterDescription:
      'Facts, history, photos and an interactive map of Nanovitsa and the sights around it in the Eastern Rhodopes.',
    schemaPlaceDescription:
      'Village in Momchilgrad Municipality, Kardzhali Province, in the Eastern Rhodopes of southern Bulgaria, close to the Thracian sanctuary of Tatul.',
    schemaTatulName: 'Tatul – Thracian sanctuary of Orpheus',
    schemaTatulDescription:
      'Thracian sanctuary with traces of settlement from about 4000 BC, associated with the cult of Orpheus, near the village of Tatul in Momchilgrad Municipality, Bulgaria.',
  },
  nav: {
    overview: 'Overview',
    explore: 'Explore',
    history: 'History',
    places: 'Places',
    map: 'Map',
    gallery: 'Gallery',
    news: 'News',
  },
  hero: {
    eyebrow: 'Kardzhali Province · Bulgaria',
    lead: 'A village in the Eastern Rhodopes of southern Bulgaria – and a guide to its neighbours: the Thracian sanctuary of Tatul, the town of Momchilgrad and the provincial capital Kardzhali.',
    exploreBtn: 'Explore the area',
    mapBtn: 'See the map',
    stats: { elevation: 'Elevation', population: 'Population (2021)', area: 'Area', postal: 'Postal code' },
  },
  overview: {
    kicker: '01 · Overview',
    title: 'A hill village in the Eastern Rhodopes',
    paragraphs: [
      'Nanovitsa (Нановица) is a village in Momchilgrad Municipality, in Kardzhali Province – Bulgaria’s southernmost province, which borders Greece.',
      'The Eastern Rhodopes around it are a landscape of rolling volcanic hills, ancient Thracian sanctuaries and mixed Bulgarian and Turkish communities. At the 2011 census, 66.2 per cent of the province’s people identified as Turkish and 30.2 per cent as Bulgarian. The region is also a haven for wildlife: almost every species of European bird of prey lives in the Eastern Rhodopes, including the rare black and Egyptian vultures.',
      'Nanovitsa itself is small – 456 residents in 2021 – but it sits in a remarkable neighbourhood. The Thracian sanctuary of Tatul is the next village along, the town of Momchilgrad is the local administrative centre, and the city of Kardzhali, the province’s capital, is the gateway to Perperikon and the Arda reservoirs.',
    ],
    note: 'Not to be confused with another village of the same name in Lovech Province, which was closed as a settlement on 24 February 2007.',
    panelTitle: 'From Nanovitsa',
    panelNote: 'Straight-line distance and direction, not driving routes.',
  },
  explore: {
    kicker: '02 · Explore',
    title: 'Four places, one landscape',
    lead: 'Switch between the village and its three closest reference points.',
    areas: [
      {
        id: 'nanovitsa',
        label: 'Nanovitsa',
        kicker: 'The village',
        title: 'Nanovitsa (Нановица)',
        lead: 'A small hill village of about 450 people in the Eastern Rhodopes, with its own mayoralty inside Momchilgrad Municipality.',
        paragraphs: [
          'Nanovitsa lies at about 308 metres above sea level and covers roughly 5.2 square kilometres. At the 2021 census it had 456 inhabitants. Road signs at the village entrance are written in both Cyrillic and Latin letters, and the postal code is 6830.',
          'The area was under Ottoman rule until 30 May 1913, and Nanovitsa came under Bulgarian administration on 10 August 1913, after the Balkan Wars. Today it is a kmetstvo – a village mayoralty – within the municipality of Momchilgrad.',
          'The village sits among the neighbouring settlements of Neofit Bozvelievo, Lale and Dzhelepsko, which are all signposted on the road out of the village. The countryside around it offers a hunting base and a dam where visitors fish, and the Thracian sanctuary at Tatul is only a short distance away.',
        ],
        note: 'Not to be confused with another village of the same name in Lovech Province, which was closed as a settlement on 24 February 2007.',
        facts: [
          ['Province', 'Kardzhali'],
          ['Municipality', 'Momchilgrad'],
          ['Elevation', '308 m'],
          ['Area', '5.2 km²'],
          ['Population', '456 (2021)'],
          ['Postal code', '6830'],
        ],
      },
      {
        id: 'tatul',
        label: 'Tatul',
        kicker: 'Thracian sanctuary',
        title: 'Tatul and the sanctuary of Orpheus',
        lead: 'A village of stone houses beside one of the most important ancient sites in the Eastern Rhodopes.',
        paragraphs: [
          'Tatul is a village in Momchilgrad Municipality, at 319 metres above sea level. Its name comes from the local Bulgarian word for Datura stramonium, and most of its houses are built from well-cut stone blocks. It had 189 inhabitants in 2005.',
          'Above the village stands an ancient Thracian sanctuary. Traces of settlement go back to about 4000 BC, and the sanctuary was in continuous use from the 19th–18th century BC. Archaeologists have found about 30 clay altars from that period, an Iron Age idol in the form of a nude male figure, and a stone sarcophagus belonging to an important Thracian ruler.',
          'Between the 4th and 1st centuries BC a stone wall was built around the hill, followed shortly afterwards by a temple and a mausoleum. More buildings were added in the 2nd–3rd centuries AD. In the late 4th or early 5th century the site was converted into a Christian domain with a defensive tower, and it was damaged by earthquakes in the 12th century BC and the 14th century AD.',
          'Head archaeologist Nikolay Ovcharov interprets the site as the sanctuary and tomb of an influential Thracian leader who was deified after death, and links it with the cult of Orpheus. The site is being developed as a tourist destination with renewed infrastructure, and a trail to the Petrified Forest starts from the village.',
        ],
        facts: [
          ['Elevation', '319 m'],
          ['Coordinates', '41°33′13″N 25°32′28″E'],
          ['Population', '189 (2005)'],
          ['From Nanovitsa', null],
          ['Earliest settlement', 'c. 4000 BC'],
          ['Sanctuary in use', 'From the 19th–18th century BC'],
        ],
      },
      {
        id: 'momchilgrad',
        label: 'Momchilgrad',
        kicker: 'The nearest town',
        title: 'Momchilgrad',
        lead: 'A town of about 7,800 people and the administrative centre for Nanovitsa, with a Turkish majority and a long Ottoman past.',
        paragraphs: [
          'Momchilgrad lies at 452 metres above sea level in the Eastern Rhodopes. At the 2011 census it had 7,831 inhabitants and is the largest Bulgarian settlement with a Turkish majority, at about 75 per cent of the population.',
          'Until 1934 the town was called Mastanli (Мастанли), and Turkish residents still call it Mestanlı. It became part of Bulgaria after the First Balkan War and was renamed after Momchil, a historical figure. Its historic mosque, with two minarets, has been in use for roughly 300 years.',
          'The Momchilgrad Museum, founded in 1969 at 2 Momchil Voyvoda Street, was the first museum of its kind in Kardzhali Province and holds about 1,200 archaeological, ethnographic and natural exhibits. Momchilgrad is also the birthplace of the swimmer Petar Stoychev, who held the record for the fastest English Channel crossing until 2012, and of the weightlifter Naim Süleymanoğlu (1967–2017).',
          'Momchilgrad Municipality covers 358.12 square kilometres and consists of the town plus 54 villages, including Nanovitsa. It had 16,263 inhabitants in 2011.',
        ],
        facts: [
          ['Elevation', '452 m'],
          ['Population', '7,831 (2011)'],
          ['Former name', 'Mastanli, until 1934'],
          ['From Nanovitsa', null],
          ['Municipality', '358.12 km², 55 settlements'],
          ['Postal code', '6800'],
        ],
      },
      {
        id: 'kardzhali',
        label: 'Kardzhali',
        kicker: 'The provincial capital',
        title: 'Kardzhali (Kırcaali)',
        lead: 'The largest city of the Eastern Rhodopes and the administrative centre of Bulgaria’s southernmost province.',
        paragraphs: [
          'Kardzhali stands on both banks of the Arda river at 275 metres above sea level, about 260 kilometres south-east of Sofia. The city had 44,071 residents in 2011 and 40,659 in 2021; the wider municipality, which includes 117 villages, had 67,846 in 2011.',
          'Its name comes from “Kırca Ali”, an 18th-century Turkish brigand leader, which is why the city is known as Kırcaali in Turkish. Ottoman rule ended on 21 October 1912, when the Bulgarian general Vasil Delov liberated the town during the First Balkan War.',
          'Kardzhali was once a centre of tobacco processing; today its economy rests mainly on retail and services, while agriculture employs a growing share of the workforce. The Regional History Museum, the Kardzhali and Studen Kladenets dams and the ancient rock city of Perperikon, about 15 kilometres to the north-east, are among its best-known landmarks. The climate is hot-summer Mediterranean, with a record high of 43.2 °C.',
          'Kardzhali Province covers 3,209.1 square kilometres and borders Greece. It is made up of seven municipalities – Ardino, Chernoochene, Dzhebel, Kardzhali, Kirkovo, Krumovgrad and Momchilgrad – and had 141,177 inhabitants in 2021. It was restored as a province in 1999, after being part of Haskovo Province from 1987.',
        ],
        facts: [
          ['Elevation', '275 m'],
          ['City population', '40,659 (2021)'],
          ['Province area', '3,209.1 km²'],
          ['From Nanovitsa', null],
          ['River', 'Arda'],
          ['Municipalities', '7 in the province'],
        ],
      },
    ],
  },
  numbers: {
    title: 'The area in numbers',
    cards: [
      {
        id: 'nanovitsa',
        name: 'Nanovitsa',
        role: 'Village',
        value: 456,
        caption: 'residents, 2021',
        rows: [
          ['Elevation', '308 m'],
          ['Area', '5.2 km²'],
        ],
      },
      {
        id: 'momchilgrad',
        name: 'Momchilgrad',
        role: 'Town and municipality seat',
        value: 7831,
        caption: 'residents, 2011',
        rows: [
          ['Elevation', '452 m'],
          ['Municipality', '16,263 residents'],
        ],
      },
      {
        id: 'kardzhali',
        name: 'Kardzhali',
        role: 'Provincial capital',
        value: 40659,
        caption: 'residents, 2021',
        rows: [
          ['Elevation', '275 m'],
          ['Municipality', '67,846 residents (2011)'],
        ],
      },
      {
        id: 'kardzhali-province',
        name: 'Kardzhali Province',
        role: 'Bulgaria’s southernmost province',
        value: 141177,
        caption: 'residents, 2021',
        rows: [
          ['Area', '3,209.1 km²'],
          ['Municipalities', '7'],
        ],
      },
    ],
  },
  timeline: {
    kicker: '03 · History',
    title: 'Seven thousand years in the hills',
    lead: 'From the first settlers on the rocky hills to the modern census – filter by place to follow one thread.',
    filters: [
      { id: 'all', label: 'Everything' },
      { id: 'tatul', label: 'Tatul' },
      { id: 'perperikon', label: 'Perperikon' },
      { id: 'nanovitsa', label: 'Nanovitsa' },
      { id: 'momchilgrad', label: 'Momchilgrad' },
      { id: 'kardzhali', label: 'Kardzhali' },
    ],
    items: [
      { id: 'perperikon-first-activity', when: 'c. 5000 BC', title: 'First human activity at Perperikon', text: 'Traces of people living on the rocky hill north-east of Kardzhali date back to about 5000 BC.' },
      { id: 'tatul-earliest-settlement', when: 'c. 4000 BC', title: 'Earliest settlement at Tatul', text: 'The oldest traces of settlement on the hill above the village of Tatul.' },
      { id: 'tatul-sanctuary-in-use', when: '19th–18th century BC', title: 'The sanctuary is in use', text: 'About 30 clay altars from this period have been found at the sanctuary, which stays in use for centuries.' },
      { id: 'perperikon-medokos', when: '424 BC', title: 'Medokos declares himself king', text: 'After the death of Sitalces, Medokos proclaims himself Thracian king from Perperikon, but is later overthrown.' },
      { id: 'tatul-wall', when: '4th–1st century BC', title: 'A wall encloses the sanctuary hill', text: 'A stone wall is built around the hill, followed shortly afterwards by a temple and a mausoleum.' },
      { id: 'tatul-new-buildings', when: '2nd–3rd century AD', title: 'New buildings at Tatul', text: 'Additional buildings are added to the sanctuary complex during the Roman period.' },
      { id: 'tatul-christian-period', when: 'Late 4th–early 5th century AD', title: 'The Christian period', text: 'The Tatul sanctuary is converted into a Christian domain with a defensive tower; Christian elements also appear at Perperikon.' },
      { id: 'perperikon-fortress-ends', when: '14th century', title: 'The medieval fortress ends', text: 'Perperikon has served as a medieval fortress up to this point. An earthquake also damages the Tatul sanctuary in the 14th century.' },
      { id: 'ali-bey-house', when: '1851', title: 'The House of Ali Bey', text: 'A house is built in the village of Manchevo, in what is now Momchilgrad Municipality.' },
      { id: 'kardzhali-liberated', when: '21 October 1912', title: 'Kardzhali is liberated', text: 'Ottoman rule ends in Kardzhali when General Vasil Delov liberates the town during the First Balkan War.' },
      { id: 'nanovitsa-joins-bulgaria', when: '30 May – 10 August 1913', title: 'Nanovitsa joins Bulgaria', text: 'Ottoman control of the area ends on 30 May. On 10 August 1913 Nanovitsa comes under Bulgarian administration.' },
      { id: 'momchilgrad-renamed', when: '1934', title: 'Mastanli becomes Momchilgrad', text: 'The town is officially renamed Momchilgrad, after Momchil, replacing its Ottoman-era name Mastanli.' },
      { id: 'kardzhali-reservoir-built', when: '1957–1963', title: 'Kardzhali Reservoir is built', text: 'Construction of the dam on the Arda begins in 1957 and the reservoir opens in 1963; work continues until 1976.' },
      { id: 'momchilgrad-museum-opens', when: '1969', title: 'Momchilgrad Museum opens', text: 'The first museum of its kind in Kardzhali Province is founded, holding about 1,200 exhibits today.' },
      { id: 'stone-mushrooms-protected', when: '13 May 1974', title: 'The Stone Mushrooms are protected', text: 'The rock formations near Beli Plast are declared a natural landmark.' },
      { id: 'kardzhali-province-restored', when: '1999', title: 'Kardzhali Province is restored', text: 'The province is re-established after twelve years as part of Haskovo Province.' },
      { id: 'perperikon-excavations-begin', when: '2000', title: 'Systematic excavations begin', text: 'Archaeologist Nikolay Ovcharov starts the systematic excavation of Perperikon, which continues today.' },
      { id: 'nanovitsa-census-2021', when: '2021', title: 'Census: 456 residents', text: 'The 2021 census records 456 people living in Nanovitsa.' },
    ],
  },
  places: {
    kicker: '04 · Places',
    title: 'What to see around Nanovitsa',
    lead: 'Ancient cities, natural monuments and historic buildings within reach of the village. Distances are measured in a straight line from Nanovitsa.',
    categories: {
      village: 'Village',
      town: 'Town',
      ancient: 'Ancient site',
      nature: 'Nature',
      water: 'Water',
      heritage: 'Heritage',
    },
    filters: [
      { id: 'all', label: 'All places' },
      { id: 'ancient', label: 'Ancient sites' },
      { id: 'nature', label: 'Nature' },
      { id: 'water', label: 'Water' },
      { id: 'heritage', label: 'Heritage' },
      { id: 'town', label: 'Towns' },
    ],
    items: [
      {
        id: 'nanovitsa',
        name: 'Nanovitsa',
        summary: 'The village this page is about, in the hills of Momchilgrad Municipality.',
        details: [],
      },
      {
        id: 'tatul',
        name: 'Tatul and the sanctuary of Orpheus',
        shortName: 'Tatul',
        summary: 'A neighbouring village with one of the best-known Thracian sanctuaries of the Eastern Rhodopes, associated with the cult of Orpheus.',
        details: [
          ['Elevation', '319 m'],
          ['Earliest settlement', 'c. 4000 BC'],
          ['Sanctuary in use', 'From the 19th–18th century BC'],
          ['Christian period', 'Late 4th–early 5th century AD'],
        ],
      },
      {
        id: 'perperikon',
        name: 'Perperikon',
        summary: 'A rock-carved ancient city on a 470 m hill, described as the largest megalith ensemble site in the Balkans.',
        details: [
          ['Location', 'About 15 km north-east of Kardzhali'],
          ['Human activity', 'Since about 5000 BC'],
          ['Fortress walls', 'Up to 2.8 m thick'],
          ['Excavations', 'Systematic since 2000, led by Nikolay Ovcharov'],
          ['Later use', 'A medieval fortress until the 14th century'],
        ],
      },
      {
        id: 'petrified-forest',
        name: 'Petrified Forest',
        summary: 'A protected natural landmark in the Gabaz Dere valley near the village of Raven, with fossilised trees around 30 million years old.',
        details: [
          ['Age', 'About 30 million years'],
          ['Origin', 'An ancient forest partly buried by a volcanic eruption'],
          ['Special feature', 'Reported to be the only such forest where the trunks still stand upright'],
          ['Trailheads', 'Raven, Tatul and Gurgulitsa'],
        ],
      },
      {
        id: 'stone-mushrooms',
        name: 'The Stone Mushrooms',
        summary: 'Mushroom-shaped rock formations east of Beli Plast, beside the road between Kardzhali and Haskovo.',
        details: [
          ['Size', 'Stalks and caps up to 2.5 m'],
          ['Rock', 'Rhyolite volcanic tuff from underwater volcanic activity in the Paleocene'],
          ['Colours', 'Pink stalks (clinoptilolite) and green caps (celadonite)'],
          ['Protected', 'Natural landmark since 13 May 1974, covering 3 hectares'],
        ],
      },
      {
        id: 'kardzhali-reservoir',
        name: 'Kardzhali Reservoir',
        summary: 'A large reservoir on the Arda river, popular for fishing and outdoor recreation, with a 103.5 m arch-gravity dam.',
        details: [
          ['Area', '16.07 km²'],
          ['Volume', '497.2 million m³ – the second largest in Bulgaria'],
          ['Dam', '103.5 m high, 402.89 m long; the only arch-gravity dam in Bulgaria'],
          ['Power', '122 MW from four turbines'],
          ['Built', 'Begun 1957, opened 1963, work continued to 1976'],
          ['Landmarks', 'The Zavoya horseshoe meander and the ruins of Patmos Fortress'],
        ],
      },
      {
        id: 'rock-niches',
        name: 'Rock niches of Neofit Bozvelievo and Lale',
        summary: 'A ritual complex of rock niches between the villages of Neofit Bozvelievo and Lale, two of which have unusual acoustic properties.',
        details: [
          ['Niches', '11, spread over two levels'],
          ['Acoustics', 'Two niches create a distinctive echo when sounds are made in front of them'],
          ['Location', 'About 2 km from Neofit Bozvelievo, near Lale'],
        ],
      },
      {
        id: 'ali-bey-house',
        name: 'House of Ali Bey, Manchevo',
        summary: 'A historic house built in 1851 in the village of Manchevo, a preserved example of the region\'s 19th-century architecture.',
        details: [
          ['Built', '1851'],
          ['Village', 'Manchevo, at 647 m elevation'],
        ],
      },
      {
        id: 'borovets',
        name: 'Borovets Reserve',
        summary: 'A protected forest area near Raven that preserves pine, oak, wild pear and juniper.',
        details: [['Protects', 'Pine, oak, wild pear and juniper woodland']],
      },
      {
        id: 'momchilgrad',
        name: 'Momchilgrad',
        summary: 'The nearest town and the seat of the municipality, with a museum founded in 1969 and a mosque with two minarets.',
        details: [
          ['Elevation', '452 m'],
          ['Population', '7,831 (2011)'],
          ['Museum', 'About 1,200 archaeological, ethnographic and natural exhibits'],
        ],
      },
      {
        id: 'kardzhali',
        name: 'Kardzhali',
        summary: 'The capital of the province, on both banks of the Arda river, with a regional history museum. Perperikon lies about 15 km to the north-east.',
        details: [
          ['Elevation', '275 m'],
          ['Population', '40,659 (2021)'],
          ['Distance from Sofia', 'About 260 km south-east'],
        ],
      },
    ],
  },
  map: {
    kicker: '06 · Map',
    title: 'Find your way around',
    lead: 'Select a place in the list, or tap a pin, to see how far it lies from Nanovitsa.',
    ariaMap: 'Interactive map of Nanovitsa and nearby places',
    youAreHere: 'You are here',
    showAll: 'Show all places',
    hint: 'Only places with published coordinates are pinned. Map data © OpenStreetMap contributors.',
    distanceSuffix: 'of Nanovitsa',
  },
  gallery: {
    kicker: '07 · Gallery',
    title: 'The landscape in pictures',
    lead: 'Click any photo to enlarge it; use the arrow keys to move through the set.',
    filters: [
      { id: 'all', label: 'All photos' },
      { id: 'nanovitsa', label: 'Nanovitsa' },
      { id: 'tatul', label: 'Tatul' },
      { id: 'momchilgrad', label: 'Momchilgrad' },
      { id: 'kardzhali', label: 'Kardzhali' },
      { id: 'region', label: 'Around the region' },
    ],
  },
  photos: {
    'village-minaret': { alt: 'A white minaret rising above trees and a red-tiled roof, with hills behind', caption: 'The minaret and red-tiled roofs, with the Rhodope hills behind' },
    'village-sign': { alt: 'Road sign reading Нановица / Nanovitsa at the edge of the village', caption: 'The village sign, in Cyrillic and Latin letters' },
    'village-view': { alt: 'Houses at the edge of Nanovitsa seen across a dry hillside', caption: 'Houses at the edge of the village' },
    'village-road': { alt: 'A country road with a stone parapet and houses on a hillside', caption: 'The road at the edge of the village' },
    'village-signpost': { alt: 'Blue road signs pointing to Neofit Bozvelievo, Lale and Dzhelepsko', caption: 'Signposts to the neighbouring villages of Neofit Bozvelievo, Lale and Dzhelepsko' },
    'tatul-panorama': { alt: 'View over the forested hills of the Eastern Rhodopes from the Tatul sanctuary', caption: 'View over the Eastern Rhodopes from the Tatul sanctuary' },
    'tatul-niche': { alt: 'A large niche cut into the rock at Tatul', caption: 'A niche cut into the rock' },
    'tatul-summit': { alt: 'The rocky summit of Tatul with forested hills in the distance', caption: 'The rocky summit of the sanctuary' },
    'tatul-rock-niche': { alt: 'A rock-cut niche in weathered stone at Tatul', caption: 'A rock-cut niche at the Tatul sanctuary' },
    'tatul-sanctuary': { alt: 'A rock-cut niche at the sanctuary with hills in the background', caption: 'A carved niche overlooking the hills' },
    'tatul-excavation': { alt: 'Pale excavated rock beneath a protective roof at Tatul', caption: 'Excavated rock face beneath a protective roof' },
    'momchilgrad-square': { alt: 'A sunlit square in Momchilgrad with buildings and trees', caption: 'The main square of Momchilgrad' },
    'momchilgrad-center': { alt: 'The paved central square of Momchilgrad with a public building at the far end', caption: 'The paved central square' },
    'kardzhali-museum': { alt: 'The Regional History Museum in Kardzhali, a stone building with a central arch', caption: 'The Regional History Museum in Kardzhali' },
    'kardzhali-view': { alt: 'View over the centre of Kardzhali with apartment blocks and a park', caption: 'The centre of Kardzhali seen from above' },
    'kardzhali-reservoir': { alt: 'Kardzhali Reservoir winding between forested hills', caption: 'Kardzhali Reservoir on the Arda river' },
    'perperikon-top': { alt: 'The rocky summit of Perperikon above forested hills', caption: 'The rock summit of Perperikon' },
    'perperikon-walls': { alt: 'A medieval stone tower at Perperikon against a sunset sky', caption: 'A medieval fortress tower at Perperikon at sunset' },
    'perperikon-complex': { alt: 'Bare rock terraces of the Perperikon archaeological complex', caption: 'The rock terraces of the Perperikon complex' },
    'stone-mushrooms': { alt: 'A mushroom-shaped rock formation on pale ground', caption: 'A mushroom-shaped rock formation near Beli Plast' },
    'stone-mushrooms-dusk': { alt: 'Mushroom-shaped rocks silhouetted against a dusk sky', caption: 'The Stone Mushrooms at dusk' },
    'studen-kladenets-dam': { alt: 'A concrete dam wall with water spilling below', caption: 'The dam wall at Studen Kladenets' },
  },
  footer: {
    tagline: 'An information page about the village of Nanovitsa in Momchilgrad Municipality, Kardzhali Province, Bulgaria – and the places around it.',
    sourcesTitle: 'Sources',
    photosTitle: 'Photos and maps',
    photosText: 'Photographs are from Wikimedia Commons and used under Creative Commons licences or in the public domain; the author and licence are credited under each photo. Map data © OpenStreetMap contributors. Distances are straight-line estimates calculated from published coordinates.',
    disclaimer: 'Facts were compiled from the public sources above and may change; please verify before you travel.',
  },
  common: {
    skip: 'Skip to content',
    menuOpen: 'Menu',
    menuClose: 'Close',
    backToTop: 'Back to top',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    readGuide: 'Read the full guide →',
    enlargePrefix: 'Enlarge photo:',
    showPhotoPrefix: 'Show photo:',
    zoomHint: 'Enlarge',
    photoViewer: 'Photo viewer',
    closeViewer: 'Close photo viewer',
    previousPhoto: 'Previous photo',
    nextPhoto: 'Next photo',
    filterPlacesAria: 'Filter places by type',
    filterPhotosAria: 'Filter photos by place',
    filterTimelineAria: 'Filter the timeline by place',
    areasTablistAria: 'Areas to explore',
    straightLineTitle: 'Straight-line distance from Nanovitsa',
    language: 'Language',
    directions: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'],
  },
  news: {
    kicker: '05 · News',
    title: 'News from Momchilgrad Municipality',
    lead: 'Recent headlines from the municipality that administers Nanovitsa, linking straight to the original articles on the municipality’s own site.',
    readMore: 'Read on momchilgrad.bg',
    viewAll: 'All news on momchilgrad.bg →',
    updated: 'Updated',
    sourceNote: 'Headlines and dates only – for the full story, follow the link to the municipality’s site. Nanovitsa.com is an independent information page and is not affiliated with the municipality.',
    untranslatedNote: 'Not yet translated – showing the original Bulgarian headline.',
  },
}
